/**
 * AI Chat Mention System - Data Mention Service
 *
 * Handles @ mentions for data references (schemas, groups, fields).
 * Provides hierarchical autocomplete suggestions with caching and deduplication.
 */

import { schemaApi, type Schema } from '@/services/api/schemaApi';
import { groupApi, type SchemaGroup, type SchemaGroupMembership } from '@/services/api/groupApi';
import { dataApi } from '@/services/api/dataApi';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import type {
  MentionContext,
  MentionSegment,
  MentionService,
  ResolvedMentionData,
  CacheEntry,
} from './types';
import {
  MAX_MENTION_SUGGESTIONS,
  MENTION_CACHE_TTL,
  FIELD_TYPE_ICONS,
  DEFAULT_FIELD_ICON,
  GROUP_ICON,
  SCHEMA_ICON,
} from './types';
import { getRecentMentions } from './RecentMentions';

const CURRENT_ORG_REQUIRED_MESSAGE = 'Current organization context is required to browse data mentions.';

function getSafeErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

/**
 * Service for handling @ data mentions
 *
 * Hierarchical structure:
 * - Level 0 (@): Groups + standalone schemas
 * - Level 1 (@group.): Schemas in group OR fields if @schema
 * - Level 2 (@group.schema.): Fields in schema within group
 */
/**
 * Accessible source from NL sources endpoint
 */
interface AccessibleSource {
  id: string;
  name: string;
  description?: string;
  tags?: string[];
  group?: string;
  groupId?: string;
  fields?: string[];
}

/**
 * Cached accessible sources structure
 */
interface AccessibleSourcesCache {
  groups: Map<string, { id: string; name: string; slug: string; schemaCount: number }>;
  schemas: AccessibleSource[];
}

function schemaToAccessibleSource(schema: Schema): AccessibleSource {
  return {
    id: schema.id,
    name: schema.name,
    description: schema.description,
    groupId: schema.groupId,
    fields: schema.fields.map((field) => field.name),
  };
}

export class DataMentionService implements MentionService {
  // Cache for schemas (by search term)
  private schemaCache: Map<string, CacheEntry<Schema[]>> = new Map();
  // Cache for groups
  private groupCache: CacheEntry<SchemaGroup[]> | null = null;
  // Cache for full schema details (by schemaId)
  private schemaDetailCache: Map<string, CacheEntry<Schema>> = new Map();
  // Cache for access-controlled NL sources (same as chat panel)
  private nlSourcesCache: CacheEntry<AccessibleSourcesCache> | null = null;
  // Pending requests for deduplication
  private pendingRequests: Map<string, Promise<unknown>> = new Map();

  /**
   * Verify organization context is available
   * @throws Error if no organization context
   */
  private ensureOrgContext(): string {
    const { currentContext } = useOrganizationStore.getState();
    if (currentContext?.type !== 'organization' || !currentContext.orgSlug?.trim()) {
      throw new Error(CURRENT_ORG_REQUIRED_MESSAGE);
    }
    return currentContext.orgSlug.trim();
  }

  /**
   * Check if a cache entry is still valid
   */
  private isCacheValid<T>(entry: CacheEntry<T> | null | undefined): entry is CacheEntry<T> {
    if (!entry) return false;
    return Date.now() - entry.timestamp < MENTION_CACHE_TTL;
  }

  /**
   * Deduplicate concurrent requests
   */
  private async deduplicateRequest<T>(key: string, factory: () => Promise<T>): Promise<T> {
    const existing = this.pendingRequests.get(key);
    if (existing) {
      return existing as Promise<T>;
    }

    const promise = factory().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Get icon for field type
   */
  private getFieldIcon(fieldType: string): string {
    const normalizedType = fieldType.toLowerCase();
    return FIELD_TYPE_ICONS[normalizedType] || DEFAULT_FIELD_ICON;
  }

  /**
   * Fetch accessible data sources using access-controlled endpoint
   * This uses the same endpoint as the chat panel for consistency
   */
  private async fetchAccessibleSources(): Promise<AccessibleSourcesCache> {
    if (this.isCacheValid(this.nlSourcesCache)) {
      return this.nlSourcesCache.data;
    }

    return this.deduplicateRequest('nl-sources', async () => {
      this.ensureOrgContext();

      const [response, groupsResult, schemasResult] = await Promise.all([
        dataApi.getNLSources({ scope: 'current' }),
        groupApi.listGroups({ limit: 100, scope: 'current' }),
        schemaApi.listSchemas({ status: 'published', limit: 50, scope: 'current' }),
      ]);
      const sources = response.sources || [];

      // Build groups map from schema data
      const groups = new Map<string, { id: string; name: string; slug: string; schemaCount: number }>();
      const groupsWithSchemaCount = new Set<string>();

      for (const group of groupsResult.data || []) {
        if (group.schemaCount != null && group.schemaCount > 0) {
          groupsWithSchemaCount.add(group.id);
        }

        groups.set(group.id, {
          id: group.id,
          name: group.name,
          slug: group.slug,
          schemaCount: group.schemaCount ?? 0,
        });
      }

      for (const source of sources) {
        if (source.groupId && source.group) {
          const existing = groups.get(source.groupId);
          if (existing) {
            if (!groupsWithSchemaCount.has(source.groupId)) {
              existing.schemaCount++;
            }
          } else {
            groups.set(source.groupId, {
              id: source.groupId,
              name: source.group,
              slug: source.group.toLowerCase().replace(/\s+/g, '-'),
              schemaCount: 1,
            });
          }
        }
      }

      const result: AccessibleSourcesCache = {
        groups,
        schemas: sources.length > 0 ? sources : schemasResult.data.map(schemaToAccessibleSource),
      };

      this.nlSourcesCache = {
        data: result,
        timestamp: Date.now(),
      };

      return result;
    });
  }

  /**
   * Fetch groups with caching
   */
  private async fetchGroups(): Promise<SchemaGroup[]> {
    if (this.isCacheValid(this.groupCache)) {
      return this.groupCache.data;
    }

    return this.deduplicateRequest('groups', async () => {
      this.ensureOrgContext();
      const result = await groupApi.listGroups({ limit: 100, scope: 'current' });
      const groups = result.data || [];

      this.groupCache = {
        data: groups,
        timestamp: Date.now(),
      };

      return groups;
    });
  }

  /**
   * Search schemas with caching
   * Uses listSchemas for empty/short search terms (backend requires 2+ chars for search)
   */
  private async searchSchemas(searchTerm: string): Promise<Schema[]> {
    // Use 'all' as cache key for empty searches to differentiate from searches with text
    const cacheKey = `search:${searchTerm || 'all'}`;
    const cached = this.schemaCache.get(cacheKey);

    if (this.isCacheValid(cached)) {
      return cached.data;
    }

    return this.deduplicateRequest(cacheKey, async () => {
      this.ensureOrgContext();

      let schemas: Schema[];

      // Use listSchemas for empty/short search terms
      // Backend /api/schemas/search requires 2+ characters
      if (!searchTerm || searchTerm.length < 2) {
        const result = await schemaApi.listSchemas({
          status: 'published',
          limit: 50,
          scope: 'current',
        });
        schemas = result.data;
      } else {
        // Use searchSchemas for proper full-text search with 2+ characters
        schemas = await schemaApi.searchSchemas({
          search: searchTerm,
          includeOwn: true,
          includeShared: true,
          includePublic: true,
          status: 'published',
          scope: 'current',
        });
      }

      this.schemaCache.set(cacheKey, {
        data: schemas,
        timestamp: Date.now(),
      });

      return schemas;
    });
  }

  /**
   * Get full schema details with caching
   */
  private async getSchemaDetails(schemaId: string): Promise<Schema | null> {
    const cached = this.schemaDetailCache.get(schemaId);

    if (this.isCacheValid(cached)) {
      return cached.data;
    }

    return this.deduplicateRequest(`schema:${schemaId}`, async () => {
      try {
        this.ensureOrgContext();
        const schema = await schemaApi.getSchema(schemaId);

        this.schemaDetailCache.set(schemaId, {
          data: schema,
          timestamp: Date.now(),
        });

        return schema;
      } catch {
        return null;
      }
    });
  }

  /**
   * Get schemas in a group using two-step fetch
   * CRITICAL: getGroupSchemas returns memberships WITHOUT full field data
   * Must fetch each schema individually for fields
   */
  private async getGroupSchemas(groupId: string): Promise<Schema[]> {
    const cacheKey = `group-schemas:${groupId}`;

    return this.deduplicateRequest(cacheKey, async () => {
      this.ensureOrgContext();

      // Step 1: Get memberships (NO fields included!)
      const memberships = await groupApi.getGroupSchemas(groupId);

      // Step 2: Fetch full schema for each active membership
      const schemaPromises = memberships
        .filter((m: SchemaGroupMembership) => m.status === 'active')
        .map((m: SchemaGroupMembership) => this.getSchemaDetails(m.schemaId));

      const schemas = await Promise.all(schemaPromises);

      // Filter out nulls (failed fetches)
      return schemas.filter((s): s is Schema => s !== null);
    });
  }

  /**
   * Find a group by slug or name from accessible sources
   */
  private async findGroup(identifier: string): Promise<{ id: string; name: string; slug: string; schemaCount: number } | null> {
    const { groups } = await this.fetchAccessibleSources();
    const lowerIdentifier = identifier.toLowerCase();

    for (const [, group] of groups) {
      if (
        group.slug.toLowerCase() === lowerIdentifier ||
        group.name.toLowerCase() === lowerIdentifier
      ) {
        return group;
      }
    }
    return null;
  }

  /**
   * Find a schema by name within a group from accessible sources
   */
  private async findSchemaInGroup(
    groupId: string,
    schemaIdentifier: string
  ): Promise<AccessibleSource | null> {
    const { schemas } = await this.fetchAccessibleSources();
    const lowerIdentifier = schemaIdentifier.toLowerCase();

    return (
      schemas.find(
        (s) =>
          s.groupId === groupId &&
          s.name.toLowerCase() === lowerIdentifier
      ) || null
    );
  }

  /**
   * Find a standalone schema by name from accessible sources
   */
  private async findStandaloneSchema(schemaIdentifier: string): Promise<AccessibleSource | null> {
    const { schemas } = await this.fetchAccessibleSources();
    const lowerIdentifier = schemaIdentifier.toLowerCase();

    const match = schemas.find(
      (s) => s.name.toLowerCase() === lowerIdentifier
    );

    if (match) {
      return match;
    }

    return null;
  }

  /**
   * Get suggestions based on mention context
   */
  public async getSuggestions(context: MentionContext): Promise<MentionSegment[]> {
    const { path, searchTerm } = context;

    try {
      switch (path.length) {
        case 0:
          // Top level: show groups + schemas
          return await this.getTopLevelSuggestions(searchTerm);
        case 1:
          // Second level: schemas in group OR fields if first segment is a schema
          return await this.getSecondLevelSuggestions(path[0], searchTerm);
        case 2:
          // Third level: fields in schema within group
          return await this.getFieldSuggestions(path[0], path[1], searchTerm);
        default:
          // Too deep, no suggestions
          return [];
      }
    } catch (error) {
      // Re-throw organization context errors (programming errors)
      if (error instanceof Error && error.message === CURRENT_ORG_REQUIRED_MESSAGE) {
        throw error;
      }
      console.error(
        '[DataMentionService] Error getting suggestions:',
        getSafeErrorMessage(error),
      );
      return [];
    }
  }

  /**
   * Get top-level suggestions (groups + schemas)
   * When no search term is provided, prepends recent data mentions
   */
  private async getTopLevelSuggestions(searchTerm: string): Promise<MentionSegment[]> {
    // If no search term, show recent at top
    if (!searchTerm) {
      const recentMentions = getRecentMentions('data');
      if (recentMentions.length > 0) {
        const freshSuggestions = await this.fetchGroupsAndSchemas('');

        // Remove duplicates (items that are in recent)
        const recentIds = new Set(recentMentions.map((m) => m.id));
        const filteredFresh = freshSuggestions.filter((s) => !recentIds.has(s.id));

        return [
          ...recentMentions.map((m) => ({ ...m, category: 'Recent' as const })),
          ...filteredFresh,
        ].slice(0, MAX_MENTION_SUGGESTIONS);
      }
    }

    return this.fetchGroupsAndSchemas(searchTerm);
  }

  /**
   * Fetch groups and schemas for suggestions
   * Uses the access-controlled NL sources endpoint (same as chat panel)
   */
  private async fetchGroupsAndSchemas(searchTerm: string): Promise<MentionSegment[]> {
    const { groups, schemas } = await this.fetchAccessibleSources();

    const suggestions: MentionSegment[] = [];
    const lowerSearch = searchTerm.toLowerCase();

    // Add matching groups
    for (const [groupId, group] of groups) {
      if (
        !searchTerm ||
        group.name.toLowerCase().includes(lowerSearch) ||
        group.slug.toLowerCase().includes(lowerSearch)
      ) {
        suggestions.push({
          value: group.name,
          reference: `@${group.slug}`,
          id: groupId,
          description: `Schema group with ${group.schemaCount} schemas`,
          icon: GROUP_ICON,
          category: 'group',
          hasChildren: true,
          metadata: {
            groupId: groupId,
            groupSlug: group.slug,
          },
        });
      }
    }

    // Add matching schemas
    for (const schema of schemas) {
      if (
        !searchTerm ||
        schema.name.toLowerCase().includes(lowerSearch)
      ) {
        suggestions.push({
          value: schema.name,
          reference: `@${schema.name.toLowerCase().replace(/\s+/g, '_')}`,
          id: schema.id,
          description: schema.description || 'Data schema',
          icon: SCHEMA_ICON,
          category: 'schema',
          hasChildren: true, // Can drill into fields
          metadata: {
            schemaId: schema.id,
            groupId: schema.groupId,
          },
        });
      }
    }

    // Sort: groups first, then schemas, both alphabetically
    suggestions.sort((a, b) => {
      if (a.category !== b.category) {
        return a.category === 'group' ? -1 : 1;
      }
      return a.value.localeCompare(b.value);
    });

    return suggestions.slice(0, MAX_MENTION_SUGGESTIONS);
  }

  /**
   * Get second-level suggestions
   * If first segment is a group: show schemas in that group
   * If first segment is a schema: show fields in that schema
   */
  private async getSecondLevelSuggestions(
    firstSegment: string,
    searchTerm: string
  ): Promise<MentionSegment[]> {
    // First, try to find it as a group
    const group = await this.findGroup(firstSegment);

    if (group) {
      // It's a group, show schemas in this group
      return this.getSchemasInGroupSuggestions(group, searchTerm);
    }

    // Not a group, try as a standalone schema
    const schema = await this.findStandaloneSchema(firstSegment);

    if (schema) {
      // It's a schema, show fields
      return this.getSchemaFieldSuggestions(schema, null, searchTerm);
    }

    return [];
  }

  /**
   * Get schemas within a group as suggestions (from accessible sources)
   */
  private async getSchemasInGroupSuggestions(
    group: { id: string; name: string; slug: string; schemaCount: number },
    searchTerm: string
  ): Promise<MentionSegment[]> {
    const { schemas } = await this.fetchAccessibleSources();
    const groupSchemas = schemas.filter((s) => s.groupId === group.id);
    const suggestions: MentionSegment[] = [];
    const lowerSearch = searchTerm.toLowerCase();

    for (const schema of groupSchemas) {
      if (
        !searchTerm ||
        schema.name.toLowerCase().includes(lowerSearch)
      ) {
        suggestions.push({
          value: schema.name,
          reference: `@${group.slug}.${schema.name.toLowerCase().replace(/\s+/g, '_')}`,
          id: schema.id,
          description: schema.description || 'Data schema',
          icon: SCHEMA_ICON,
          category: 'schema',
          hasChildren: true, // Can drill into fields
          metadata: {
            schemaId: schema.id,
            groupId: group.id,
            groupSlug: group.slug,
          },
        });
      }
    }

    suggestions.sort((a, b) => a.value.localeCompare(b.value));
    return suggestions.slice(0, MAX_MENTION_SUGGESTIONS);
  }

  /**
   * Get field suggestions for a schema (from accessible sources)
   * Note: NL sources include field names as strings
   */
  private async getSchemaFieldSuggestions(
    schema: AccessibleSource,
    group: { id: string; name: string; slug: string; schemaCount: number } | null,
    searchTerm: string
  ): Promise<MentionSegment[]> {
    const suggestions: MentionSegment[] = [];
    const lowerSearch = searchTerm.toLowerCase();
    const schemaSlug = schema.name.toLowerCase().replace(/\s+/g, '_');
    const basePath = group ? `@${group.slug}.${schemaSlug}` : `@${schemaSlug}`;

    // NL sources returns fields as string array
    for (const fieldName of schema.fields || []) {
      if (!searchTerm || fieldName.toLowerCase().includes(lowerSearch)) {
        suggestions.push({
          value: fieldName,
          reference: `${basePath}.${fieldName}`,
          id: fieldName,
          description: 'Field',
          icon: DEFAULT_FIELD_ICON,
          category: 'field',
          hasChildren: false, // Fields are leaf nodes
          metadata: {
            schemaId: schema.id,
            groupId: group?.id,
            groupSlug: group?.slug,
            fieldName: fieldName,
          },
        });
      }
    }

    suggestions.sort((a, b) => a.value.localeCompare(b.value));
    return suggestions.slice(0, MAX_MENTION_SUGGESTIONS);
  }

  /**
   * Get field suggestions for group.schema path
   */
  private async getFieldSuggestions(
    groupSegment: string,
    schemaSegment: string,
    searchTerm: string
  ): Promise<MentionSegment[]> {
    const group = await this.findGroup(groupSegment);

    if (!group) {
      return [];
    }

    const schema = await this.findSchemaInGroup(group.id, schemaSegment);

    if (!schema) {
      return [];
    }

    return this.getSchemaFieldSuggestions(schema, group, searchTerm);
  }

  /**
   * Resolve a data mention reference to full data
   *
   * @param reference - The reference path without @ (e.g., "greenhouse.temp.reading")
   */
  public async resolveReference(reference: string): Promise<ResolvedMentionData | null> {
    const segments = reference.split('.').filter(Boolean);

    if (segments.length === 0) {
      return null;
    }

    try {
      // Try as group first
      const group = await this.findGroup(segments[0]);

      if (group) {
        if (segments.length === 1) {
          // Just the group
          return {
            type: 'data',
            reference,
            group: {
              id: group.id,
              name: group.name,
              slug: group.slug,
            },
          };
        }

        // Group + schema
        const schema = await this.findSchemaInGroup(group.id, segments[1]);

        if (!schema) {
          return null;
        }

        if (segments.length === 2) {
          // Group + schema, no field
          return {
            type: 'data',
            reference,
            group: {
              id: group.id,
              name: group.name,
              slug: group.slug,
            },
            schema: {
              id: schema.id,
              name: schema.name,
              displayName: schema.name,
              description: schema.description,
              fields: schema.fields?.map((fieldName: string) => ({
                name: fieldName,
                type: 'unknown',
              })),
            },
          };
        }

        // Group + schema + field
        const fieldName = schema.fields?.find(
          (f: string) => f.toLowerCase() === segments[2].toLowerCase()
        );

        if (!fieldName) {
          return null;
        }

        return {
          type: 'data',
          reference,
          group: {
            id: group.id,
            name: group.name,
            slug: group.slug,
          },
          schema: {
            id: schema.id,
            name: schema.name,
            displayName: schema.name,
            description: schema.description,
          },
          field: {
            name: fieldName,
            type: 'unknown',
          },
        };
      }

      // Not a group, try as standalone schema
      const standaloneSchema = await this.findStandaloneSchema(segments[0]);

      if (!standaloneSchema) {
        return null;
      }

      if (segments.length === 1) {
        // Just the schema
        return {
          type: 'data',
          reference,
          schema: {
            id: standaloneSchema.id,
            name: standaloneSchema.name,
            displayName: standaloneSchema.name,
            description: standaloneSchema.description,
            fields: standaloneSchema.fields?.map((fieldName: string) => ({
              name: fieldName,
              type: 'unknown',
            })),
          },
        };
      }

      // Schema + field
      const fieldName = standaloneSchema.fields?.find(
        (f: string) => f.toLowerCase() === segments[1].toLowerCase()
      );

      if (!fieldName) {
        return null;
      }

      return {
        type: 'data',
        reference,
        schema: {
          id: standaloneSchema.id,
          name: standaloneSchema.name,
          displayName: standaloneSchema.name,
          description: standaloneSchema.description,
        },
        field: {
          name: fieldName,
          type: 'unknown',
        },
      };
    } catch (error) {
      console.error(
        '[DataMentionService] Error resolving reference:',
        getSafeErrorMessage(error),
      );
      return null;
    }
  }

  /**
   * Clear all caches
   */
  public clearCache(): void {
    this.schemaCache.clear();
    this.groupCache = null;
    this.schemaDetailCache.clear();
    this.nlSourcesCache = null;
  }
}
